import styles from './styles.module.scss';
import { PageWrapper } from 'components-antd';
import { Wrapper } from 'components';
import { Tabs } from 'components-antd';
import { SideBar } from '../components/SideBar';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAgentDetailsEffect, requestGetTeamListEffect } from 'store/effects';
import { useHistory, useParams } from 'react-router-dom';
import { getAgentDetailsSelector } from 'store/selectors/agentDetail';
import { Icons } from '../components/icons';
import classNames from 'classnames';
import { Activity } from '../components/Activity';
import Clients from './Clients';
import Portfolio from './Portfolio';
import Tasks from './Tasks';
import Transactions from './Transactions';
import Notifications from './Notifications';
import { Documents } from '../components/Documents';
import { getAgentTeamRoleSelector } from 'store/selectors/agentTeamDetail';
import { getUserId } from 'store/selectors/user';
import { TEAM_BASIC } from 'settings/constants/roles';
import { routes } from 'settings/navigation/routes';
import { requestGetAgentDetailsAction } from 'store/actions/agentDetail';
import { showErrorMessage } from 'helpers';
import { isProfileExpandedSectionOPened } from 'store/selectors/app';

export const MemberProfile = () => {
  const { id } = useParams() as any;
  const dispatch = useDispatch();
  const details = useSelector(getAgentDetailsSelector);
  const [selectedTab, setSelectedTab] = useState('1');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const contact = details?.data || {};
  const agentRole = useSelector(getAgentTeamRoleSelector);
  const currentUserId = useSelector(getUserId);
  const history = useHistory();
  const isProfileDrawerOpen = useSelector(isProfileExpandedSectionOPened);

  const getConnectionDetails = useCallback(() => {
    setIsLoading(true);
    dispatch(
      getAgentDetailsEffect({ id }, {}, (err) => {
        setIsLoading(false);
        if (err) {
          showErrorMessage('Unable to fetch team member profile.');
          history.push(routes.index);
        }
      }),
    );
  }, [dispatch, id]);

  useEffect(() => {
    setSelectedTab('1');
    getConnectionDetails();
  }, [id]);

  useEffect(() => {
    if (isProfileDrawerOpen) history.push(routes.index);
  }, [isProfileDrawerOpen]);

  useEffect(() => {
    if (isLoading || !contact?.Id) return;

    const canViewProfile =
      agentRole !== TEAM_BASIC && contact.IsManagedByCurrentUser && contact.Id !== currentUserId;

    if (!canViewProfile) history.push(routes.index);
  }, [isLoading, contact]);

  useEffect(() => {
    dispatch(requestGetTeamListEffect());
    return () => {
      dispatch(requestGetAgentDetailsAction({}));
    };
  }, []);

  const tabs = [
    {
      key: '1',
      id: 'activity',
      label: (
        <span className={classNames(styles.tabLabel, { [styles.active]: selectedTab === '1' })}>
          <Icons variant={Icons.ACTIVITY} /> Activity
        </span>
      ),
      children: <Activity isAgent={true} />,
    },
    {
      key: '2',
      id: 'clients',
      label: (
        <span className={classNames(styles.tabLabel, { [styles.active]: selectedTab === '2' })}>
          <Icons variant={Icons.CLIENTS} /> Clients
        </span>
      ),
      children: <Clients />,
    },
    {
      key: '3',
      id: 'portfolio',
      label: (
        <span className={classNames(styles.tabLabel, { [styles.active]: selectedTab === '3' })}>
          <Icons variant={Icons.PORTFOLIO} /> Portfolio
        </span>
      ),
      children: <Portfolio contactId={Number(id)} />,
    },
    {
      key: '4',
      id: 'tasks',
      label: (
        <span className={classNames(styles.tabLabel, { [styles.active]: selectedTab === '4' })}>
          <Icons variant={Icons.KITS} /> Tasks
        </span>
      ),
      children: <Tasks contactId={Number(id)} />,
    },
    {
      key: '5',
      id: 'transactions',
      label: (
        <span className={classNames(styles.tabLabel, { [styles.active]: selectedTab === '5' })}>
          <Icons variant={Icons.TRANSACTIONS} /> Transactions
        </span>
      ),
      children: <Transactions contactId={Number(id)} />,
    },
    {
      key: '6',
      id: 'files',
      label: (
        <span className={classNames(styles.tabLabel, { [styles.active]: selectedTab === '6' })}>
          <Icons variant={Icons.FILES} /> Files
        </span>
      ),
      children: (
        <Documents
          isClient={false}
          userId={Number(id)}
          contactName={`${contact.FirstName}${contact.LastName ? ` ${contact.LastName}` : ''}`}
        />
      ),
    },
    {
      key: '7',
      id: 'notifications',
      label: (
        <span
          className={classNames(styles.tabLabel, {
            [styles.active]: selectedTab === '7',
            [styles.activeFill]: selectedTab === '7',
          })}
        >
          <Icons variant={Icons.NOTIFICATIONS} /> Notifications
        </span>
      ),
      children: (
        <Notifications
          contactId={Number(id)}
          contactName={`${contact.FirstName}${contact.LastName ? ` ${contact.LastName}` : ''}`}
          contactPhones={contact.Phones}
          contactEmail={contact.Email}
          notificationPref={contact.NotificationPreferences}
          disableAll={contact.NotificationsSelfUpdated}
        />
      ),
    },
  ];

  return (
    <PageWrapper
      version={2}
      mainPageContentStyle={styles.mainPageContent}
      pageContentClassName={styles.pageContent}
    >
      <SideBar contact={contact} isPending={isLoading} isAgent={true} addresses={[]} />
      <Wrapper isPending={isLoading} className={styles.content}>
        <Tabs
          className={styles.tabs}
          items={tabs as any}
          defaultActiveKey={selectedTab}
          onTabClick={(id) => {
            setSelectedTab(id);
          }}
        />
      </Wrapper>
    </PageWrapper>
  );
};
